import type { GetNotificationsPayload, Notification, ReadNotificationPayload } from '../../types/index.js';
import { baseApi } from '../base-api.js';

type NotificationsResponse = { notifications: Notification[] };
type ReadNotificationResponse = { notification: Notification; message: string };

const notificationListTags = (result: Notification[] | undefined) =>
  result
    ? [
        ...result.map(({ id }) => ({ type: 'Notification' as const, id })),
        { type: 'Notification' as const, id: 'LIST' },
      ]
    : [{ type: 'Notification' as const, id: 'LIST' }];

/**
 * Mirrors `apps/server/src/modules/notification` (`notificationController`). The server has no
 * unread-count or mark-all-read route — both apps currently derive the unread badge client-side
 * by filtering `notifications` where `read_at === null`, so there's nothing to add here beyond
 * list + mark-one-as-read; flagged in the report as a gap worth a server-side endpoint later if
 * the unread count needs to be fetched without pulling the full list.
 */
export const notificationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getNotifications: builder.query<Notification[], GetNotificationsPayload | undefined>({
      query: (payload) => ({ path: ['notifications'], method: 'get', query: payload }),
      transformResponse: (response: NotificationsResponse) => response.notifications,
      providesTags: (result) => notificationListTags(result),
    }),

    readNotification: builder.mutation<Notification, ReadNotificationPayload>({
      query: ({ notification_id }) => ({
        path: ['notifications', { param: 'notification_id', value: notification_id }, 'read'],
        method: 'put',
      }),
      transformResponse: (response: ReadNotificationResponse) => response.notification,
      invalidatesTags: (_result, _error, { notification_id }) => [
        { type: 'Notification', id: notification_id },
        { type: 'Notification', id: 'LIST' },
      ],
    }),
  }),
});

export const { useGetNotificationsQuery, useReadNotificationMutation } = notificationApi;
